import { http } from "../../utils/config";

const customerAPI = {
  // Get all customers with pagination and search
  getCustomers: (params = {}) => {
    const {
      page = 1,
      per_page = 15,
      search = '',
      orgId = '',
      groupId = '',
      status = '',
      sort_by = 'created_at',
      sort_order = 'desc'
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order,
      ...(search && { search }),
      ...(orgId && { orgId }),
      ...(groupId && { groupId }),
      ...(status !== '' && { status })
    });

    return http.get(`/api/v1/customers?${queryParams}`);
  },

  getCustomerAll: (params = {}) => {
    return http.get(`/api/v1/customers/all`, { params });
  },

  // Get customer by ID
  getCustomerById: (id) => {
    return http.get(`/api/v1/customers/${id}`);
  },

  // Create new customer
  createCustomer: (customerData) => {
    return http.post('/api/v1/customers', customerData);
  },

  // Update customer
  updateCustomer: (id, customerData) => {
    return http.put(`/api/v1/customers/${id}`, customerData);
  },

  // Delete customer
  deleteCustomer: (id) => {
    return http.delete(`/api/v1/customers/${id}`);
  },

  // Xóa nhiều cư dân đã tích chọn. Dùng POST thay vì DELETE-có-body
  // vì một số proxy loại bỏ body của request DELETE.
  bulkDeleteCustomers: (ids) => {
    return http.post('/api/v1/customers/bulk-delete', { ids });
  },

  // Update customer status
  updateCustomerStatus: (id, status) => {
    return http.patch(`/api/v1/customers/${id}/status`, {
      status: status
    });
  },

  // Export customers to Excel
  exportCustomers: (params = {}) => {
    const queryParams = new URLSearchParams(params);
    return http.get(`/api/v1/datamanagement/export/customers?${queryParams}`, {
      responseType: 'blob'
    });
  },

  // Import customers from Excel
  importCustomers: (file, organization_ids, groupId = null, isAssetCreate = false) => {
    const formData = new FormData();
    formData.append('file', file);
    
    if(organization_ids.length > 0){
      organization_ids.forEach(id => {
        formData.append("organization_ids[]", id);
      });
    }

    if(groupId){
      formData.append('group_id', groupId);
    }

    if (isAssetCreate) {
      formData.append('is_asset_create', isAssetCreate ? 1 : 0);
    }
    
    return http.post('/api/v1/datamanagement/import/customers', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  // Get customer statistics
  getCustomerStats: () => {
    return http.get('/api/v1/customers/stats');
  },

  // Search customers with advanced filters
  searchCustomers: (filters) => {
    return http.post('/api/v1/customers/search', filters);
  },

  // Check if phone exists
  checkPhone: (phone) => {
    return http.get(`/api/v1/customers/check-phone?phone=${phone}`);
  }
};

// Helper functions for handling API responses
export const customerHelpers = {
  // Extract customer data from API response
  extractCustomerData: (response) => {
    if (response.data && response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  },

  // Extract paginated customer list from API response
  extractCustomerList: (response) => {
    if (response.data && response.data.success) {
      const data = response.data.data;
      return {
        customers: data.data || [],
        pagination: {
          current: data.current_page,
          pageSize: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to,
          lastPage: data.last_page,
          nextPageUrl: data.next_page_url,
          prevPageUrl: data.prev_page_url
        }
      };
    }
    throw new Error(response.data?.message || 'Unknown error occurred');
  },

  // Format customer data for form submission
  formatCustomerData: (formData) => {
    return {
      name: formData.name?.trim(),
      email: formData.email?.trim().toLowerCase(),
      phone: formData.phone?.trim(),
      dob: formData.dob,
      sonha: formData.sonha?.trim(),
      address: formData.address?.trim(),
      status: formData.status ?? true,
      metadata: formData.metadata || {},
      identity_account: formData.identity_account?.trim(),
      discount_type: formData.discount_type?.trim(),
      discount_value: formData.discount_value?.trim(),
      organization_ids: formData.organization_ids || [],
      represent_name: formData.represent_name?.trim(),
    };
  },

  // Validate customer data before submission
  validateCustomerData: (customerData) => {
    const errors = {};

    if (!customerData.name?.trim()) {
      errors.name = 'Tên khách hàng không được để trống';
    }

    // if (!customerData.email?.trim()) {
    //   errors.email = 'Email không được để trống';
    // } else if (!/\S+@\S+\.\S+/.test(customerData.email)) {
    //   errors.email = 'Email không hợp lệ';
    // }

    if (customerData.email && !/\S+@\S+\.\S+/.test(customerData.email)) {
      errors.email = 'Email không hợp lệ';
    }

    if (!customerData.phone?.trim()) {
      errors.phone = 'Số điện thoại không được để trống';
    } else if (!/^[0-9]{9,12}$/.test(customerData.phone.replace(/\D/g, ''))) {
      errors.phone = 'Số điện thoại không hợp lệ (phải từ 9-12 chữ số)';
    }

    if (customerData.dob) {
      const birthDate = new Date(customerData.dob);
      const today = new Date();
      if (birthDate > today) {
        errors.dob = 'Ngày sinh không thể lớn hơn ngày hiện tại';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
};

// Error handling wrapper
export const withErrorHandling = (apiFunction) => {
  return async (...args) => {
    try {
      const response = await apiFunction(...args);
      return response;
    } catch (error) {
      console.error('API Error:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        switch (status) {
          case 400:
            throw new Error(data.message || 'Dữ liệu không hợp lệ');
          case 401:
            throw new Error('Bạn cần đăng nhập để thực hiện thao tác này');
          case 403:
            throw new Error('Bạn không có quyền thực hiện thao tác này');
          case 404:
            throw new Error('Không tìm thấy khách hàng');
          case 422: {
            // StoreCustomerRequest/UpdateCustomerRequest trả {success:false, errors:{field:[...]}}
            // và KHÔNG có `message`, nên chỉ đọc data.message thì lỗi theo trường bị nuốt
            // mất, người dùng chỉ thấy câu chung chung. Gắn kèm để form hiện đúng ô.
            const validationError = new Error(data.message || 'Dữ liệu không hợp lệ');
            validationError.fieldErrors = data.errors || null;
            throw validationError;
          }
          case 500:
            throw new Error(data.message || "Lỗi server, vui lòng thử lại sau");
          default:
            throw new Error(data.message || 'Đã có lỗi xảy ra');
        }
      } else if (error.request) {
        // Network error
        throw new Error('Không thể kết nối đến server');
      } else {
        // Other error
        throw new Error(error.message || 'Đã có lỗi xảy ra');
      }
    }
  };
};

// Wrapped API functions with error handling
export const customerAPIWithErrorHandling = {
  getCustomers: withErrorHandling(customerAPI.getCustomers),
  getCustomerAll: withErrorHandling(customerAPI.getCustomerAll),
  getCustomerById: withErrorHandling(customerAPI.getCustomerById),
  createCustomer: withErrorHandling(customerAPI.createCustomer),
  updateCustomer: withErrorHandling(customerAPI.updateCustomer),
  deleteCustomer: withErrorHandling(customerAPI.deleteCustomer),
  bulkDeleteCustomers: withErrorHandling(customerAPI.bulkDeleteCustomers),
  updateCustomerStatus: withErrorHandling(customerAPI.updateCustomerStatus),
  exportCustomers: withErrorHandling(customerAPI.exportCustomers),
  importCustomers: withErrorHandling(customerAPI.importCustomers),
  getCustomerStats: withErrorHandling(customerAPI.getCustomerStats),
  searchCustomers: withErrorHandling(customerAPI.searchCustomers),
  checkPhone: withErrorHandling(customerAPI.checkPhone)
};

export default customerAPI;